import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Backdrop
} from '@mui/material';
import { Shield } from '@mui/icons-material';
import ProductForm from './ProductForm';
import { connect } from 'react-redux';
import useCurrencyOptions from '../../../../hooks/useCurrencyOptions';

// Overview tab of the single-product editor. Matches the ASPin prototype's
// inline-editable form feel: the product config is edited directly here (via
// ProductForm), not through a read-only card + modal. All save wiring is
// unchanged — ProductForm's submit calls handleEditProduct -> onProductInfoEdit.
const Product = (props) => {
  const [productData, setProductData] = useState(null);

  // Notifications
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  useEffect(() => {
    if (props.currentProduct) {
      setProductData(props.currentProduct);
    } else if (props.productsData && props.productsData.currentProduct) {
      setProductData(props.productsData.currentProduct);
    }
  }, [props.currentProduct, props.productsData && props.productsData.currentProduct]);

  const currencyOptions = useCurrencyOptions(
    props.countryState,
    props.dispatch
  );

  useEffect(() => {
    if (props.productSuccess) {
      setSnackbarMessage('Product created successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setTimeout(() => {
        props.resetProductSuccess && props.resetProductSuccess();
      }, 100);
    }
  }, [props.productSuccess]);

  useEffect(() => {
    if (props.productError) {
      setSnackbarMessage(`Error: ${props.productError.message || 'Product operation failed'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setTimeout(() => {
        props.resetProductError && props.resetProductError();
      }, 100);
    }
  }, [props.productError]);

  useEffect(() => {
    if (props.editProductSuccess) {
      setSnackbarMessage('Product updated successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setTimeout(() => {
        props.resetEditProductSuccess && props.resetEditProductSuccess();
      }, 100);
    }
  }, [props.editProductSuccess]);

  useEffect(() => {
    if (props.editProductError) {
      setSnackbarMessage(`Error: ${props.editProductError.message || 'Product update failed'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setTimeout(() => {
        props.resetEditProductError && props.resetEditProductError();
      }, 100);
    }
  }, [props.editProductError]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleEditProduct = (updatedData) => {
    setProductData({ ...productData, ...updatedData });
    if (props.onProductInfoEdit) {
      props.onProductInfoEdit(updatedData);
    }
  };

  if (!productData) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 400,
          p: 4,
        }}
      >
        <Shield sx={{ fontSize: 48, color: "#6b7280", mb: 2 }} />
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          No Product Data
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Please select a product or create a new one to view details
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Inline editable product configuration (prototype Overview feel) */}
      <ProductForm
        onSubmit={handleEditProduct}
        initialData={productData}
        isEdit={true}
        currencyOptions={currencyOptions}
      />

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={props.productLoader || props.editProductLoader}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress color="inherit" />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Processing product info operation...
          </Typography>
        </Box>
      </Backdrop>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default connect((state) => ({
  productsData: state.newProducts,
  globalData: state.global,
  countryState: state.country,
}))(Product);
